import * as React from 'react'
import { cn } from '@cocoonspace/ui/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'

const buttonVariants = cva(
	[
		'inline-flex items-center justify-center gap-1 whitespace-nowrap transition-colors',
		'disabled:pointer-events-none disabled:opacity-50',
		'focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring',
		"shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
	],
	{
		compoundVariants: [
			{
				class: 'h-[40px] px-[40px]',
				icon: false,
				size: 'default',
				variant: ['default', 'gradient', 'outline', 'outlinePrimary', 'ghost'],
			},
			{
				class: 'rounded-full',
				size: 'default',
				variant: ['default', 'gradient', 'outline', 'outlinePrimary', 'ghost'],
			},
			{
				class: 'h-[32px] rounded px-[16px]',
				icon: false,
				size: 'sm',
				variant: ['default', 'gradient', 'outline', 'outlinePrimary', 'ghost'],
			},
			{
				class: 'size-[40px]',
				icon: true,
				size: 'default',
			},
			{
				class: 'size-[32px]',
				icon: true,
				size: 'sm',
			},
		],
		defaultVariants: {
			icon: false,
			size: 'default',
			variant: 'default',
		},
		variants: {
			icon: {
				true: '',
			},
			size: {
				default: 'text-base',
				sm: 'text-sm',
			},
			variant: {
				default: [
					'rounded bg-primary font-bold text-primary-foreground',
					'hover:bg-[#eb3e34]',
				],
				ghost: [
					'rounded border border-transparent font-bold text-foreground',
					'hover:border-slate-200 hover:bg-slate-200',
				],
				gradient: [
					'rounded bg-linear-to-r from-primary to-[#ff5c28] font-bold text-primary-foreground',
					'hover:from-primary-[#ff5c28] hover:to-primary-[#ff5c28] disabled:bg-primary disabled:text-white',
				],
				link: 'text-foreground underline underline-offset-[6px] hover:decoration-transparent',
				outline: [
					'rounded border border-foreground font-bold text-foreground hover:border-primary',
					'hover:text-primary',
				],
				outlinePrimary: [
					'rounded border border-primary font-bold text-primary',
					'hover:bg-primary hover:text-primary-foreground',
				],
				text: 'text-foreground hover:text-primary',
				textPrimary: 'font-bold text-primary',
			},
		},
	},
)

export interface ButtonProps
	extends
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, icon, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot.Root : 'button'

		return (
			<Comp
				className={cn(buttonVariants({ className, icon, size, variant }))}
				ref={ref}
				{...props}
				type={props.type || 'button'}
			/>
		)
	},
)

Button.displayName = 'Button'

export { Button, buttonVariants }
